import React, { useEffect, useState } from "react";
import { getCached } from "@/lib/cache";
import { PageHeader, StatusChip, LoadingBlock } from "@/components/Common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
} from "recharts";
import { Chart } from "@/components/Chart";
import { fmtCurrency, fmtPct } from "@/lib/utils-app";
import { ArrowUpRight, ArrowDownRight, AlertTriangle, BedDouble, Wrench, Star, Users as UsersIcon, WifiOff } from "lucide-react";

function Kpi({ label, value, delta, suffix, sparkline }) {
    const positive = (delta || 0) >= 0;
    return (
        <div className="rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-sm)]" data-testid="kpi-card">
            <div className="text-xs text-muted-foreground">{label}</div>
            <div className="mt-1 flex items-baseline gap-2">
                <div className="text-2xl font-semibold tabular" data-testid="kpi-card-value">
                    {value}
                    {suffix}
                </div>
                {delta !== undefined && (
                    <div
                        data-testid="kpi-card-delta"
                        className={`text-xs px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5 ${positive ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" : "bg-[hsl(var(--destructive)/0.1)] text-[hsl(var(--destructive))]"}`}
                    >
                        {positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                        {Math.abs(delta)}%
                    </div>
                )}
            </div>
            {sparkline && sparkline.length > 0 && (
                <div className="-mx-1 mt-2">
                    <Chart height={40}>
                        <LineChart data={sparkline}>
                            <Line type="monotone" dataKey="v" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                        </LineChart>
                    </Chart>
                </div>
            )}
        </div>
    );
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [source, setSource] = useState("network");

    useEffect(() => {
        getCached("/admin/dashboard").then(({ data, source }) => {
            if (data) {
                setData(data);
                setSource(source);
            }
        });
    }, []);

    if (!data) {
        return (
            <>
                <PageHeader title="Dashboard" subtitle="Loading today's operations…" />
                <LoadingBlock rows={4} />
            </>
        );
    }

    const k = data.kpis;
    const spark = data.occupancy_sparkline.map((s) => ({ d: s.d, v: s.occ }));

    return (
        <>
            <PageHeader
                eyebrow="Today · Operations"
                title="Good day, team."
                subtitle={`Snapshot for ${data.date}. ${k.in_house} in-house · ${k.arrivals_today} arrivals · ${k.departures_today} departures.`}
                actions={
                    source === "cache" ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.08)] px-2 py-0.5 text-xs text-[hsl(var(--warning))]" data-testid="dashboard-cache-banner">
                            <WifiOff className="w-3 h-3" /> Offline · showing cached data
                        </span>
                    ) : null
                }
            />

            {/* KPI hero strip */}
            <div className="kpi-hero rounded-2xl border border-border p-4 sm:p-5 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpi-strip">
                    <Kpi label="Occupancy" value={k.occupancy} suffix="%" delta={4} sparkline={spark} />
                    <Kpi label="ADR" value={fmtCurrency(k.adr)} delta={2} sparkline={spark} />
                    <Kpi label="RevPAR" value={fmtCurrency(k.revpar)} delta={6} sparkline={spark} />
                    <Kpi label="Revenue today" value={fmtCurrency(k.revenue_today)} delta={3} sparkline={spark} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
                <Card className="lg:col-span-8">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Arrivals & Departures</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div className="text-xs text-muted-foreground mb-2">
                                    Arrivals today ({data.arrivals.length})
                                </div>
                                <div className="space-y-1.5" data-testid="dashboard-arrivals">
                                    {data.arrivals.length === 0 && (
                                        <div className="text-xs text-muted-foreground">None today.</div>
                                    )}
                                    {data.arrivals.map((r) => (
                                        <div
                                            key={r.id}
                                            className="flex items-center justify-between text-sm bg-secondary/60 rounded-lg px-3 py-2"
                                        >
                                            <div className="truncate">
                                                <span className="font-medium">{r.guest_name}</span>
                                                <span className="text-muted-foreground"> · Room {r.room_number}</span>
                                            </div>
                                            <StatusChip status={r.status} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground mb-2">
                                    Departures today ({data.departures.length})
                                </div>
                                <div className="space-y-1.5" data-testid="dashboard-departures">
                                    {data.departures.length === 0 && (
                                        <div className="text-xs text-muted-foreground">None today.</div>
                                    )}
                                    {data.departures.map((r) => (
                                        <div
                                            key={r.id}
                                            className="flex items-center justify-between text-sm bg-secondary/60 rounded-lg px-3 py-2"
                                        >
                                            <div className="truncate">
                                                <span className="font-medium">{r.guest_name}</span>
                                                <span className="text-muted-foreground"> · Room {r.room_number}</span>
                                            </div>
                                            <StatusChip status={r.status} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-4">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-[hsl(var(--warning))]" /> Alerts & Tasks
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {data.alerts.length === 0 && (
                            <div className="text-sm text-muted-foreground">All systems calm.</div>
                        )}
                        {data.alerts.map((a, i) => (
                            <div key={i} className="text-sm rounded-lg bg-secondary/60 px-3 py-2">
                                {a.text}
                            </div>
                        ))}
                        <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="rounded-lg border border-border p-3 text-center">
                                <BedDouble className="w-4 h-4 mx-auto text-muted-foreground" />
                                <div className="text-lg font-semibold tabular mt-1">{k.open_housekeeping}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Housekeeping</div>
                            </div>
                            <div className="rounded-lg border border-border p-3 text-center">
                                <Wrench className="w-4 h-4 mx-auto text-muted-foreground" />
                                <div className="text-lg font-semibold tabular mt-1">{k.open_maintenance}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Maintenance</div>
                            </div>
                            <div className="rounded-lg border border-border p-3 text-center">
                                <Star className="w-4 h-4 mx-auto text-muted-foreground" />
                                <div className="text-lg font-semibold tabular mt-1">{k.avg_rating}</div>
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Avg rating</div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-12">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base">Latest reviews</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {data.recent_reviews.map((rv) => (
                                <div key={rv.id} className="rounded-lg border border-border p-3">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-sm font-medium">{rv.guest_name}</div>
                                        <StatusChip status={rv.sentiment} />
                                    </div>
                                    <div className="text-xs text-muted-foreground mb-1">
                                        {rv.source} · {rv.rating}/5
                                    </div>
                                    <div className="text-sm line-clamp-3">{rv.text}</div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
